import React, { useState } from "react";
import { cn } from "@/lib/utils";
import { useSubmitFeedback } from "@/opportunity/api";
import { toast } from "sonner";

type AssessmentChoice = "confirmed" | "different" | "not-found";

interface AssessmentCardProps {
  opportunityId: number;
  className?: string;
}

export function AssessmentCard({ opportunityId, className }: AssessmentCardProps) {
  const [state, setState] = useState<"idle" | "followup" | "submitted">("idle");
  const [choice, setChoice] = useState<AssessmentChoice | null>(null);
  const [note, setNote] = useState("");
  const submitFeedback = useSubmitFeedback(opportunityId);

  const handleSelect = (selected: AssessmentChoice) => {
    setChoice(selected);
    setState("followup");
  };

  const handleSubmit = () => {
    const meterMap: Record<AssessmentChoice, "thumbs_up" | "thumbs_down"> = {
      confirmed: "thumbs_up",
      different: "thumbs_down",
      "not-found": "thumbs_down",
    };

    const prefixMap: Record<AssessmentChoice, string> = {
      confirmed: "[Confirmed on site]",
      different: "[Different cause found]",
      "not-found": "[Not found on site]",
    };

    submitFeedback.mutate(
      {
        useful_meter: meterMap[choice!],
        text: `${prefixMap[choice!]} ${note}`.trim(),
        comment: choice === "different" ? "other" : undefined,
      },
      {
        onSuccess: () => {
          setState("submitted");
          toast.success("Assessment submitted. Thank you!");
        },
        onError: () => {
          toast.error("Failed to submit assessment. Please try again.");
        },
      }
    );
  };

  if (state === "submitted") {
    return (
      <div className={cn("bg-copilot-info-bg border border-copilot-info-border rounded-[var(--card-radius)] p-[var(--space-md)] text-center", className)}>
        <div className="text-2xl mb-2">
          {choice === "confirmed" ? "✓" : choice === "different" ? "📝" : "📋"}
        </div>
        <p className="text-sm font-medium text-copilot-text-primary">
          {choice === "confirmed" && "Issue confirmed — thank you."}
          {choice === "different" && "Thank you — your input helps improve our detection."}
          {choice === "not-found" && "Noted — we'll continue monitoring."}
        </p>
      </div>
    );
  }

  return (
    <div className={cn("bg-copilot-info-bg border border-copilot-info-border rounded-[var(--card-radius)] p-[var(--space-md)]", className)}>
      <h2 className="text-[var(--heading-card-size)] font-semibold mb-[var(--space-md)]">
        Have you checked this issue?
      </h2>

      {state === "idle" && (
        <div className="flex flex-col gap-2">
          <button
            onClick={() => handleSelect("confirmed")}
            className="flex items-center gap-2 min-h-[var(--touch-target)] px-[var(--space-md)] py-3 rounded-lg text-sm font-medium text-left w-full bg-white border-[1.5px] border-copilot-healthy-border text-copilot-healthy hover:bg-copilot-healthy-bg transition-colors"
          >
            Confirmed — Issue found on site
          </button>
          <button
            onClick={() => handleSelect("different")}
            className="flex items-center gap-2 min-h-[var(--touch-target)] px-[var(--space-md)] py-3 rounded-lg text-sm font-medium text-left w-full bg-white border-[1.5px] border-copilot-warning-border text-copilot-warning hover:bg-copilot-warning-bg transition-colors"
          >
            <span className="flex-1">Different Cause — Issue exists, different root cause</span>
            <span className="text-[var(--overline-size)] bg-copilot-warning-bg text-copilot-warning px-1.5 py-0.5 rounded shrink-0">
              Most helpful
            </span>
          </button>
          <button
            onClick={() => handleSelect("not-found")}
            className="flex items-center gap-2 min-h-[var(--touch-target)] px-[var(--space-md)] py-3 rounded-lg text-sm font-medium text-left w-full bg-white border-[1.5px] border-copilot-neutral-border text-copilot-text-secondary hover:bg-copilot-neutral-bg transition-colors"
          >
            Not Found on Site — Could not locate issue
          </button>
        </div>
      )}

      {state === "followup" && choice === "confirmed" && (
        <div className="mt-[var(--space-md)]">
          <div className="text-2xl text-copilot-healthy mb-2">✓</div>
          <p className="text-sm font-medium text-copilot-healthy mb-3">
            Issue confirmed — thank you.
          </p>
          <textarea
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="Optional note (e.g., additional details observed)"
            className="w-full min-h-[80px] border-[1.5px] border-copilot-neutral-border rounded-lg p-[var(--space-md)] resize-y text-sm font-sans"
          />
          <button
            onClick={handleSubmit}
            disabled={submitFeedback.isPending}
            className="mt-[var(--space-md)] w-full bg-copilot-brand text-white font-semibold text-sm rounded-full px-5 py-2.5 min-h-[var(--touch-target)] hover:bg-copilot-brand-dark transition-colors disabled:opacity-50"
          >
            {submitFeedback.isPending ? "Submitting..." : "Submit"}
          </button>
        </div>
      )}

      {state === "followup" && choice === "different" && (
        <div className="mt-[var(--space-md)]">
          <p className="text-sm font-medium mb-2">What did you find instead?</p>
          <textarea
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="Describe the root cause you found..."
            required
            className="w-full min-h-[80px] border-[1.5px] border-copilot-neutral-border rounded-lg p-[var(--space-md)] resize-y text-sm font-sans"
          />
          <button
            onClick={handleSubmit}
            disabled={submitFeedback.isPending || !note.trim()}
            className="mt-[var(--space-md)] w-full bg-copilot-brand text-white font-semibold text-sm rounded-full px-5 py-2.5 min-h-[var(--touch-target)] hover:bg-copilot-brand-dark transition-colors disabled:opacity-50"
          >
            {submitFeedback.isPending ? "Submitting..." : "Submit"}
          </button>
        </div>
      )}

      {state === "followup" && choice === "not-found" && (
        <div className="mt-[var(--space-md)]">
          <p className="text-sm font-medium mb-2">
            Noted — we'll continue monitoring.
          </p>
          <textarea
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="Optional note"
            className="w-full min-h-[80px] border-[1.5px] border-copilot-neutral-border rounded-lg p-[var(--space-md)] resize-y text-sm font-sans"
          />
          <button
            onClick={handleSubmit}
            disabled={submitFeedback.isPending}
            className="mt-[var(--space-md)] w-full bg-copilot-brand text-white font-semibold text-sm rounded-full px-5 py-2.5 min-h-[var(--touch-target)] hover:bg-copilot-brand-dark transition-colors disabled:opacity-50"
          >
            {submitFeedback.isPending ? "Submitting..." : "Submit"}
          </button>
        </div>
      )}
    </div>
  );
}
